import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Snackbar } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { SiteTakeoffProvider } from './SiteTakeoffContext';
import SiteTakeoffHeader from './header/SiteTakeoffHeader';
import Sidebar from './sidebar/Sidebar';
import MapContainer from './map/MapContainer';
import PropertySettingsDrawer from './drawer/PropertySettingsDrawer';
import BulkEditDrawer from './drawer/BulkEditDrawer';
import SiteTakeoffTour from './SiteTakeoffTour';
import useJobsite from './hooks/useJobsite';
import useAreas from './hooks/useAreas';
import useAreaRendering from './hooks/useAreaRendering';
import useAreaEditing from './hooks/useAreaEditing';
import useAreaDrawing from './hooks/useAreaDrawing';
import useOverlays from './hooks/useOverlays';
import useGeofence from './hooks/useGeofence';
import usePropertyLines from './hooks/usePropertyLines';
import useRestrictedAreas from './hooks/useRestrictedAreas';
import useJobsiteDrawing from './hooks/useJobsiteDrawing';
import useDataLayerVisibility from './hooks/useDataLayerVisibility';
import {
  SITE_TAKEOFF_V2_TOUR_STEPS,
  SITE_TAKEOFF_V2_TOUR_STORAGE_KEY,
} from './tourConfig';
import { AREA_TYPES } from '../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    background: '#f5f5f5',
    position: 'relative',
    overflow: 'hidden',
  },
  body: {
    flex: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  mapArea: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
    overflow: 'hidden',
  },
}));

const SiteTakeoffV3 = (props) => {
  const classes = useStyles();
  const { jobsiteId } = useParams();
  const history = useHistory();

  // Map refs
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const tileLayerRef = useRef(null);

  // UI state
  const [activeMode, setActiveMode] = useState('areas');
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState(null);
  const [draggableAreaId, setDraggableAreaId] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [toastMsg, setToastMsg] = useState('');
  const [hiddenAreaIds, setHiddenAreaIds] = useState(new Set());

  const toggleAreaVisibility = useCallback((areaId) => {
    setHiddenAreaIds(prev => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  }, []);

  // Data layer visibility (extracted to hook)
  const { dataLayersVisible } = useDataLayerVisibility();

  // Header pickers state
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedJobsite, setSelectedJobsite] = useState(null);

  // Load full jobsite data
  const { jobsite: loadedJobsite, legacyAreas, loading: jobsiteLoading } = useJobsite(props.url, jobsiteId);

  // Load takeoff / areas
  const {
    areas, takeoffs, takeoff, loading: areasLoading, saving, dirty,
    addArea, updateArea, deleteArea, duplicateArea, saveTakeoff,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    updateProductionRates, importLegacyAreas,
  } = useAreas(props.url, loadedJobsite);

  // Show legacy import when we have old jobsite.areas but no takeoffs yet
  const hasLegacyAreas =
    (legacyAreas?.length || 0) > 0 && takeoffs.length === 0 && !areasLoading;

  // Render areas on the map
  const handleAreaMoved = useCallback((areaId, newPaths) => {
    updateArea(areaId, { paths: newPaths });
  }, [updateArea]);

  // layerMapRef is shared between rendering and editing hooks
  const layerMapRef = useRef({});

  // Shape editing must be declared before rendering so editingShapeAreaId is available
  const {
    editingAreaId: editingShapeAreaId,
    startEdit: startEditShape,
    stopEdit: stopEditShape,
  } = useAreaEditing({
    mapRef, layerMapRef, areas, updateArea,
  });

  useAreaRendering({
    mapRef, drawnItemsRef, areas,
    onAreaClick: setEditingAreaId,
    visible: dataLayersVisible.takeoffAreas !== false,
    activeAreaId: editingAreaId,
    draggableAreaId,
    onAreaMoved: handleAreaMoved,
    hiddenAreaIds,
    showMeasurements: dataLayersVisible.measurements === true,
    editingShapeAreaId,
    layerMapRef,
  });

  const zoomToArea = useCallback((areaId) => {
    const layer = layerMapRef.current[areaId];
    const map = mapRef.current;
    if (layer && map) {
      try { map.fitBounds(layer.getBounds().pad(0.3), { animate: true }); } catch (_) {}
    }
  }, [layerMapRef, mapRef]);

  // Area drawing tools
  const { startDraw, snapping, setSnapping } = useAreaDrawing({
    mapRef, drawnItemsRef,
    onAreaDrawn: (newAreaDraft) => {
      const created = addArea(newAreaDraft);
      setEditingAreaId(created.id);
    },
  });

  // Draw an exclusion zone for a parent area
  const handleDrawExclusion = useCallback((parentAreaId) => {
    setEditingAreaId(null);
    startDraw('polygon', {
      parentAreaId,
      exclusionType: 'other',
      areaType: AREA_TYPES.DO_NOT_SERVICE,
      name: 'Exclusion',
    });
  }, [startDraw]);

  // Jobsite-layer drawing (property lines, restricted areas)
  const { startPolygonDraw, startPolygonEdit, confirmEdit, cancelEdit, undoEdit, editing: editingShape } = useJobsiteDrawing(mapRef);

  // Geofence
  const geofence = useGeofence({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.geofence,
  });

  // Property lines
  const propertyLines = usePropertyLines({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.propertyLines,
  });

  // Restricted areas
  const restrictedAreas = useRestrictedAreas({
    url: props.url, jobsite: loadedJobsite, mapRef,
    visible: dataLayersVisible.restrictedAreas,
  });

  // Plan overlays (takeoff-scoped)
  const overlayManager = useOverlays({
    url: props.url, mapRef,
    jobsite: loadedJobsite,
    initialOverlays: takeoff?.overlays,
  });

  // Keep the header picker in sync with the loaded jobsite
  useEffect(() => {
    if (!loadedJobsite?._id) return;
    setSelectedJobsite({
      _id: loadedJobsite._id,
      name: loadedJobsite.name,
      address: loadedJobsite.address,
      city: loadedJobsite.city,
      state: loadedJobsite.state,
      client: loadedJobsite.client,
    });
    if (loadedJobsite.client) {
      const clientObj = typeof loadedJobsite.client === 'object'
        ? loadedJobsite.client
        : { _id: loadedJobsite.client };
      setSelectedClient(clientObj);
    }
  }, [loadedJobsite]);

  // Wrap saveTakeoff to inject live overlay geometry at save time + show feedback
  const saveTakeoffWithOverlays = useCallback(async (name) => {
    const liveOverlays = overlayManager?.serializeForSave?.() || [];
    const result = await saveTakeoff(name, { overlays: liveOverlays });
    setToastMsg(result ? 'Takeoff saved' : 'Failed to save');
    return result;
  }, [saveTakeoff, overlayManager]);

  const handleJobsiteChange = useCallback(async (jobsite) => {
    // Aspire-only result: promote to a local Jobsite first, then proceed with
    // the returned Jobsite as the selection. The server creates the Jobsite,
    // links matchedJobsiteId, and stamps aspirePropertyId on the new doc.
    if (jobsite?.source === 'aspire' && jobsite.aspirePropertyId) {
      try {
        const resp = await axios.post(
          `${props.url}/api/customer/aspire-property/${jobsite.aspirePropertyId}/promote`
        );
        if (resp.data?.success && resp.data.jobsite) {
          // eslint-disable-next-line no-param-reassign
          jobsite = resp.data.jobsite;
        } else {
          console.error('Aspire promote failed:', resp.data);
          return;
        }
      } catch (err) {
        console.error('Aspire promote error:', err);
        return;
      }
    }

    setSelectedJobsite(jobsite);
    setEditingAreaId(null);
    if (jobsite?.client) {
      const clientObj = typeof jobsite.client === 'object' ? jobsite.client : { _id: jobsite.client };
      setSelectedClient(clientObj);
    }
    if (jobsite?._id) {
      history.push(`/site-takeoff-v3/${jobsite._id}`);
    }
  }, [history, props.url]);

  const handleClientChange = useCallback((client) => {
    setSelectedClient(client);
    setSelectedJobsite(null);
    setEditingAreaId(null);
    if (!client) {
      history.push('/site-takeoff-v3');
    }
  }, [history]);

  // Setup status
  const setupStatus = {
    areasMapped: areas.length > 0,
    geofenceSet: !!geofence?.confirmed,
    propertyLinesVerified:
      (propertyLines.countyLines.length + propertyLines.manualLines.length) > 0,
  };
  const isSetupComplete = Object.values(setupStatus).every(Boolean);

  // Tour
  const currentTourStep = SITE_TAKEOFF_V2_TOUR_STEPS[tourStepIndex];

  const markTourSeen = useCallback(() => {
    try { localStorage.setItem(SITE_TAKEOFF_V2_TOUR_STORAGE_KEY, '1'); } catch (_) {}
  }, []);

  const closeTour = useCallback((markSeen = true) => {
    setTourOpen(false);
    setTourStepIndex(0);
    setSettingsDrawerOpen(false);
    if (markSeen) markTourSeen();
  }, [markTourSeen]);

  const launchTour = useCallback(() => {
    stopEditShape();
    setEditingAreaId(null);
    setActiveMode('areas');
    setSettingsDrawerOpen(false);
    setTourStepIndex(0);
    setTourOpen(true);
  }, [stopEditShape]);

  useEffect(() => {
    if (!loadedJobsite?._id) return;
    try { if (localStorage.getItem(SITE_TAKEOFF_V2_TOUR_STORAGE_KEY)) return; } catch (_) {}
    const timer = setTimeout(() => { launchTour(); }, 900);
    return () => clearTimeout(timer);
  }, [loadedJobsite?._id, launchTour]);

  useEffect(() => {
    if (!tourOpen || !currentTourStep) return;
    stopEditShape();
    setEditingAreaId(null);
    if (currentTourStep.id === 'geofence-section' || currentTourStep.id === 'property-lines-section') {
      setSettingsDrawerOpen(true);
    } else {
      setSettingsDrawerOpen(false);
    }
    if (currentTourStep.id === 'projects-upload') {
      setActiveMode('projects');
    } else {
      setActiveMode('areas');
    }
  }, [tourOpen, currentTourStep, stopEditShape]);

  const handleTourNext = useCallback(() => {
    if (tourStepIndex >= SITE_TAKEOFF_V2_TOUR_STEPS.length - 1) { closeTour(true); return; }
    setTourStepIndex(tourStepIndex + 1);
  }, [tourStepIndex, closeTour]);

  const handleTourBack = useCallback(() => {
    if (tourStepIndex <= 0) return;
    setTourStepIndex(tourStepIndex - 1);
  }, [tourStepIndex]);

  const handleTourTargetMissing = useCallback(() => {
    if (tourStepIndex >= SITE_TAKEOFF_V2_TOUR_STEPS.length - 1) { closeTour(true); return; }
    setTourStepIndex(tourStepIndex + 1);
  }, [tourStepIndex, closeTour]);

  // Assemble context value
  const contextValue = useMemo(() => ({
    url: props.url, clients: props.clients,
    jobsite: loadedJobsite, areas, takeoffs, takeoff, saving, dirty,
    addArea, updateArea, deleteArea, duplicateArea, saveTakeoff: saveTakeoffWithOverlays,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    updateProductionRates, importLegacyAreas,
    legacyAreas, hasLegacyAreas,
    activeMode, setActiveMode,
    editingAreaId, setEditingAreaId,
    editingShapeAreaId, startEditShape, stopEditShape,
    draggableAreaId, setDraggableAreaId,
    bulkEditOpen, setBulkEditOpen,
    startDraw, snapping, setSnapping, handleDrawExclusion,
    zoomToArea, overlayManager,
    hiddenAreaIds, toggleAreaVisibility,
    settingsDrawerOpen, setSettingsDrawerOpen,
    geofence, propertyLines, restrictedAreas,
    startPolygonDraw, startPolygonEdit,
    editingShape, confirmEdit, cancelEdit, undoEdit,
    setupStatus, mapRef, drawnItemsRef, tileLayerRef,
    dataLayersVisible,
  }), [
    props.url, props.clients, loadedJobsite,
    areas, takeoffs, takeoff, saving, dirty,
    addArea, updateArea, deleteArea, duplicateArea, saveTakeoffWithOverlays,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    updateProductionRates, importLegacyAreas,
    legacyAreas, hasLegacyAreas,
    activeMode, editingAreaId,
    editingShapeAreaId, startEditShape, stopEditShape,
    draggableAreaId, bulkEditOpen,
    startDraw, snapping, setSnapping, handleDrawExclusion,
    zoomToArea, overlayManager,
    hiddenAreaIds, toggleAreaVisibility,
    settingsDrawerOpen,
    geofence, propertyLines, restrictedAreas,
    startPolygonDraw, startPolygonEdit,
    editingShape, confirmEdit, cancelEdit, undoEdit,
    setupStatus, dataLayersVisible,
  ]);

  return (
    <SiteTakeoffProvider value={contextValue}>
      <div className={classes.root}>
        <SiteTakeoffHeader
          url={props.url}
          clients={props.clients}
          selectedClient={selectedClient}
          selectedJobsite={selectedJobsite}
          onClientChange={handleClientChange}
          onJobsiteChange={handleJobsiteChange}
          setupComplete={isSetupComplete}
          onSettingsClick={() => setSettingsDrawerOpen(true)}
          onGuideClick={launchTour}
        />

        <div className={classes.body}>
          <div className={classes.mapArea}>
            <MapContainer
              jobsite={loadedJobsite}
              loading={jobsiteLoading || areasLoading}
              mapRef={mapRef}
              drawnItemsRef={drawnItemsRef}
              tileLayerRef={tileLayerRef}
              editingShape={editingShape}
              onConfirmEdit={confirmEdit}
              onCancelEdit={cancelEdit}
              onUndoEdit={undoEdit}
            />
          </div>

          <Sidebar />

          <BulkEditDrawer
            open={bulkEditOpen}
            onClose={() => setBulkEditOpen(false)}
            areas={areas}
            onUpdateArea={updateArea}
          />

          <PropertySettingsDrawer
            open={settingsDrawerOpen}
            onClose={() => setSettingsDrawerOpen(false)}
            url={props.url}
            jobsite={loadedJobsite}
            setupStatus={setupStatus}
            geofence={geofence}
            propertyLines={propertyLines}
            restrictedAreas={restrictedAreas}
            startPolygonDraw={startPolygonDraw}
            startPolygonEdit={startPolygonEdit}
            areas={areas}
            rateOverrides={takeoff?.productionRates || {}}
            onUpdateRates={updateProductionRates}
          />
        </div>

        <Snackbar
          open={!!toastMsg}
          autoHideDuration={3000}
          onClose={() => setToastMsg('')}
          message={toastMsg}
        />
        <SiteTakeoffTour
          open={tourOpen}
          step={currentTourStep}
          stepIndex={tourStepIndex}
          totalSteps={SITE_TAKEOFF_V2_TOUR_STEPS.length}
          onNext={handleTourNext}
          onBack={handleTourBack}
          onClose={() => closeTour(true)}
          onTargetMissing={handleTourTargetMissing}
        />
      </div>
    </SiteTakeoffProvider>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  clients: state.clients,
});

export default connect(mapStateToProps)(SiteTakeoffV3);
